import { type ReactNode, useLayoutEffect, useRef, useState } from "react";

import { MOTION_EASE_OUT } from "@/blueprint/tokens/Motion";
import { useOnScreen } from "@/hooks/useOnScreen";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/utils";

/**
 * The page's general copy interaction: content is not there, and as it comes
 * into view it fades in and rises a short way into place. Leaving reverses it.
 *
 * Figma carries this as three screen recordings of an Apple product page rather
 * than as a description — `9178:52090`, `9178:52796` and `9178:52790`, the last
 * annotated "Please see this reference for all the general copy interactions".
 * They are video fills, which the REST API only ever hands back as a poster
 * frame, so the pattern is taken from what the recordings show and the numbers
 * below are ours.
 *
 * Unlike [ScrollRevealText], which scrubs a line word by word against scroll
 * position, this is a threshold: in view or not. Reach for that one when the
 * reveal should track the scrollbar, and this one when it should simply arrive.
 */
type ScrollRevealProps = {
  children: ReactNode;
  /**
   * Whether the content leaves again on the way out. Off by default, since most
   * copy should stay once read; the Hey Matic bubbles are the exception, where
   * one line leaving is what makes room for the next.
   */
  reversible?: boolean;
  /** Shrinks the trigger box so content arrives before it touches the edge. */
  rootMargin?: string;
  /** Holds the reveal, unlatched, until the page is ready to show it. */
  armed?: boolean;
  /** Waits this long before revealing; leaving is never delayed. */
  delayMs?: number;
  className?: string;
};

/** Far enough to read as movement, short enough not to read as a slide.
 *  Exported so above-the-fold copy can play the same arrival on mount. */
export const REVEAL_RISE_PX = 24;
export const REVEAL_DURATION_MS = 700;

const ScrollReveal = ({
  children,
  reversible = false,
  rootMargin = "-15% 0px",
  armed = true,
  delayMs = 0,
  className,
}: ScrollRevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const onScreen = useOnScreen(ref, { rootMargin });
  const seen = useRef(false);
  const measuredOnMotion = useRef(false);
  const [heldVisible, setHeldVisible] = useState(false);

  /* Motion only turns on after hydration, before the observer has reported.
     Content already in the viewport at that moment is held visible so it does
     not flash out and back in; the observer takes over once it reports. */
  useLayoutEffect(() => {
    if (reducedMotion || measuredOnMotion.current) {
      return;
    }
    measuredOnMotion.current = true;

    const rect = ref.current?.getBoundingClientRect();
    if (
      armed &&
      rect !== undefined &&
      rect.top < window.innerHeight &&
      rect.bottom > 0
    ) {
      seen.current = true;
      setHeldVisible(true);
    }
  }, [reducedMotion, armed]);

  if (onScreen && armed) {
    seen.current = true;
  }
  if (onScreen && heldVisible) {
    setHeldVisible(false);
  }

  const shown =
    reducedMotion ||
    heldVisible ||
    (armed && (reversible ? onScreen : seen.current));

  return (
    <div
      ref={ref}
      className={cn(className)}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: shown ? 1 : 0,
              /* Held content must not take focus while it is invisible. Only
                 the held case: an unarmed reveal is the caller's own gate, so
                 the default scroll reveal keeps its existing behaviour. */
              visibility: armed ? "visible" : "hidden",
              transform: `translateY(${shown ? 0 : REVEAL_RISE_PX}px)`,
              transitionProperty: "opacity, transform, visibility",
              transitionDuration: `${REVEAL_DURATION_MS}ms`,
              transitionDelay: shown ? `${delayMs}ms` : "0ms",
              transitionTimingFunction: MOTION_EASE_OUT,
            }
      }
    >
      {children}
    </div>
  );
};

export default ScrollReveal;
